<template>
  <a
    class="c-result flex gap-1 p-2 border-rounded-1 cursor-pointer position-relative"
    :class="{ 'c-result--selected': selected }"
    tabindex="0"
    @click="open(item)"
    @keyup.enter="open(item)"
  >
    <CImage
      v-if="(item.picture_url || item.type === 'file') && !appItem"
      class="c-result__image h-14 border-rounded-1 overflow-hidden flex-0"
      cover
      :file-data="item"
      object-fit="cover"
      :src="item.picture_url || ''"
      width="73"
    />

    <div class="flex flex-col p-2 gap-2 justify-center overflow-hidden">
      <div class="overflow-hidden flex items-center gap-2">
        <CIcon
          v-if="appItem"
          class="flex-grow-0 flex-shrink-0 mr-2"
          :color="themeVars.primary4"
          :icon="appItem.icon || 'browser'"
          :size="24"
        />
        <CIcon v-else-if="!item.picture_url && item.type !== 'file'" class="c-result__icon" :icon="icon" :size="12" />
        <div>
          <div class="c-result__title font-bold truncate">{{ item.name }}</div>
          <div
            v-if="item.folder && item.folder.id !== rootFolderId"
            class="inline-flex items-center gap-1 color-text2 font-size-[14px] leading-trim-both text-edge-cap font-normal leading-normal tracking-[0.4px]"
            @click.stop="emit('openFolder', item.folder.id)"
          >
            <CIcon icon="folder" />
            {{ folderDisplayName }}
          </div>
          <div v-else-if="appItem?.description" class="truncate color-text2 font-size-[14px]">
            {{ appItem.description }}
          </div>
        </div>
      </div>

      <div v-if="item.headline" class="c-result__headline" v-html="item.headline" />
    </div>
    <CIcon
      v-if="appItem?.isOverlay"
      class="position-absolute bottom-0 right-5 top-0 m-a"
      :color="themeVars.primary4"
      fa-type="far"
      icon="window-restore"
      :size="16"
    />
  </a>
</template>

<script lang="ts" setup>
import type { Folder } from '@openapi'

import { GlobalSearchResultType, type GlobalSearchResult } from '@lib/components/CGlobalSearch/CGlobalSearch.types'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'
import { type AppFile } from '@lib/types/app'
import { computed } from 'vue'

interface Props {
  item: GlobalSearchResult
  selected: boolean
  icon: string
  rootFolderId?: string
}

const themeVars = useThemeVars()
const { parseNameOnly } = useFolderNameDescription()
const props = defineProps<Props>()

const emit = defineEmits<{
  open: [i: GlobalSearchResult]
  openFolder: [folderId: Folder['id']]
}>()

const open = (item: GlobalSearchResult) => {
  emit('open', item)
}

const appItem = computed(() => {
  if (props.item.type === GlobalSearchResultType.App) {
    const app = props.item as unknown as AppFile
    return {
      icon: app.app_metadata?.icon,
      name: app.app_metadata?.display_name,
      description: app.app_metadata?.description,
      isOverlay: app.app_metadata?.module?.overlay_app?.enabled,
    }
  }

  return null
})

const folderDisplayName = computed(() => {
  if (!props.item.folder) return ''
  return parseNameOnly(props.item.folder.name)
})
</script>

<style lang="scss" scoped>
.c-result {
  background: v-bind('themeVars.primary6');
  color: v-bind('themeVars.text');

  &.c-result--selected,
  &:focus {
    background: v-bind('themeVars.primary5');
    outline: none;
  }

  &__image {
    background: v-bind('themeVars.primary5');
  }

  &__icon {
    color: v-bind('themeVars.text');
    font-size: 12px;
  }

  &__title {
    font-size: 14px;
    line-height: 1.2;
  }

  &__headline {
    font-size: 12px;
    color: v-bind('themeVars.text2');
    line-height: 1.2;
  }
}
</style>
